import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { ChevronIcon } from "vega-icons";

import { useAuth } from "@vega-api/custom/api/hooks/useAuth";
import { organisationActions } from "@vega-api/custom/stores/organisations.store";
import { FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import { type ManageableLimitedPartnersResponse } from "@vega-api/generated/types/manageableLimitedPartnersResponse";
import { useGetManageableLimitedPartners } from "@vega-api/generated/user-management-v1/user-management-v1";
import { SecondaryButton } from "@vega-core/components/Buttons/SecondaryButton";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { Avatar, AvatarFallback } from "@vega-core/components/shadcn/avatar";
import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { CommonPaths } from "@vega-modules/paths";
import { REDIRECT_STORAGE_KEY } from "@vega-modules/scenes/QueryRedirectHandler/QueryRedirectHandler";
import { WelcomeHeader } from "@vega-modules/scenes/Welcome/WelcomeHeader";

const getLimitedPartnerIdFromRedirect = (): string | null => {
  const saved = localStorage.getItem(REDIRECT_STORAGE_KEY);

  if (!saved) {
    return null;
  }

  try {
    const parsed = new URL(saved, window.location.origin);

    return parsed.searchParams.get("limitedPartnerId");
  } catch {
    return null;
  }
};

export const SelectOrgPage = () => {
  const navigate = useNavigate();
  const hasAutoSelectedRef = useRef(false);
  const { logout, isLoading: isLogoutLoading } = useAuth();

  const { data, isLoading } = useGetManageableLimitedPartners({
    query: {
      select: (data) => data.data,
    },
  });
  const hasOrgs = isTruthy(data) && data.length > 0;

  if (hasOrgs) {
    organisationActions.setOrganisationList(data);
  }

  const handleSelectOrg = (org: ManageableLimitedPartnersResponse) => {
    data && organisationActions.setOrganisationList(data);
    organisationActions.setOrganisation(org);
    navigate(CommonPaths.Home);
  };

  // Auto-select organization from redirect URL if limitedPartnerId is present
  useEffect(() => {
    if (hasAutoSelectedRef.current || !hasOrgs) {
      return;
    }

    const limitedPartnerId = getLimitedPartnerIdFromRedirect();

    if (limitedPartnerId) {
      const matchingOrg = data.find((org) => org.id === limitedPartnerId);

      if (matchingOrg) {
        hasAutoSelectedRef.current = true;
        organisationActions.setOrganisationList(data);
        organisationActions.setOrganisation(matchingOrg);
        navigate(CommonPaths.Home);
      }
    }
  }, [data, hasOrgs, navigate]);

  const SelectOrganization = () => (
    <div className="border border-border/primary bg-background/primary rounded-default">
      <h1 className="body-extra-large-semibold p-6">
        {m.titles_selectOrganization()}
      </h1>
      <div className="list-none divide-y-1 border-t-1">
        {data?.map((org) => (
          <div
            className="flex gap-4 border-border/primary text-ellipsis text-text/primary hover:bg-background/component justify-start group items-center px-6 py-5 relative cursor-pointer"
            data-testid={org.id}
            key={org.id}
            onClick={() => handleSelectOrg(org)}
          >
            <Avatar className="h-8 w-8">
              <AvatarFallback>{org.name}</AvatarFallback>
            </Avatar>
            <span className="body-base-semibold whitespace-nowrap text-ellipsis overflow-hidden">
              {org.name}
            </span>
            <div className="w-6 h-6 ml-auto relative">
              <ChevronIcon className="ml-auto text-text/secondary rotate-270 min-h-6 min-w-6 xs:block sm:hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-dvh bg-background/secondary overflow-auto">
      <WelcomeHeader />

      <main className="flex-1 flex items-center justify-center">
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="text-center px-4 pb-4 sm:p-0 w-full sm:w-[548px]">
            {hasOrgs ? (
              <SelectOrganization />
            ) : (
              <div className="flex flex-col items-center gap-6">
                <h1>{m.titles_noOrganizations()}</h1>
                <SecondaryButton
                  dataTestId="logout-button"
                  isLoading={isLogoutLoading}
                  onClick={() =>
                    logout({
                      isGlobal: true,
                      savePath: false,
                      source: FrontendPortal.LP_PORTAL,
                    })
                  }
                >
                  {m.common_logOut()}
                </SecondaryButton>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};
